import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { ConnectorDetails } from "../../types"
import { RTK_SLICE_API_LIST } from "../../common/constants"
import { baseQuery, getTags } from "../api"
import bbApi from "../../api"

const reducerPath = "connectors"

export const connectorsApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath, "connector-logs"],
  endpoints: builder => ({
    findConnectors: builder.query({
      queryFn: async (params: {
        brand_id?: string
        owner?: string
        type?: string
        active?: number
        direction?: string
        data_type?: string
        limit?: number
        offset?: number
      }) => {
        const response = await bbApi.connectors.find(params)
        return { data: response.connectors ?? [] }
      },
      providesTags: result => getTags(reducerPath, "id", result)
    }),
    findConnectorDetails: builder.query({
      queryFn: async (params: { id: string }) => {
        const data = await bbApi.connectors.findDetails(params.id)
        return { data }
      },
      providesTags: (result, error, arg) => [{ type: reducerPath, id: arg.id }]
    }),
    updateOrCreateConnector: builder.mutation({
      queryFn: async (payload: Partial<ConnectorDetails>) => {
        // Remove "id" field if it's empty which means it's CREATE action
        if (!payload.id) {
          delete payload.id
        }
        const data = await bbApi.connectors.updateOrCreate(payload)
        return { data }
      },
      invalidatesTags: (result, error, arg) => [
        { type: reducerPath, id: arg.id },
        { type: reducerPath, id: RTK_SLICE_API_LIST },
        { type: "connector-logs", id: RTK_SLICE_API_LIST }
      ]
    }),
    deleteConnector: builder.mutation({
      queryFn: async (id: string) => {
        const data = await bbApi.connectors.delete(id)
        return { data }
      },
      invalidatesTags: (result, error, id) => [
        { type: reducerPath, id },
        { type: reducerPath, id: RTK_SLICE_API_LIST },
        { type: "connector-logs", id: RTK_SLICE_API_LIST }
      ]
    }),
    findConnectorLogs: builder.query({
      queryFn: async (params: {
        connector_id?: string
        record_id?: string
        status?: string
        limit?: number
        offset?: number
      }) => {
        const response = await bbApi.connectors.findLogs(params)
        return {
          data: {
            logs: response.logs ?? [],
            count: response.count ?? 0
          }
        }
      },
      providesTags: result => {
        if (result?.logs) {
          return [
            ...getTags("connector-logs", "id", result.logs),
            { type: "connector-logs", id: RTK_SLICE_API_LIST }
          ]
        }
        return [{ type: "connector-logs", id: RTK_SLICE_API_LIST }]
      }
    }),
    testConnector: builder.mutation({
      queryFn: async (params: {
        connector_id: string
        record_id?: string
        test_data?: any
      }) => {
        const data = await bbApi.connectors.testConnection(params)
        return { data }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "connector-logs", id: RTK_SLICE_API_LIST }
      ]
    })
  })
})

export const {
  useFindConnectorsQuery,
  useFindConnectorDetailsQuery,
  useUpdateOrCreateConnectorMutation,
  useDeleteConnectorMutation,
  useFindConnectorLogsQuery,
  useTestConnectorMutation
} = connectorsApi
